import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import { KATEGORIEN, KATEGORIENAME, posteingangsstand, type Kategorie, type Posteingangsstand } from '../api/support';
import {
  PLATZHALTER,
  bausteinAendern,
  bausteinAnlegen,
  bausteinEntfernen,
  bausteine as ladeBausteine,
  type Bausteineingabe,
  type Textbaustein,
} from '../api/textbausteine';
import { alsZeitpunkt } from '../datum';
import { Abschlussreihe } from '../komponenten/Abschlussreihe';
import { AbTablet } from '../komponenten/AbTablet';
import { Dialog } from '../komponenten/Dialog';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { markenklasse } from '../marken';

/**
 * DIE TEXTBAUSTEINE DER POSTFÄCHER (SUP-20; Auftrag CRM-178).
 *
 * Ein Baustein gilt für ein Postfach oder für alle, für eine Kategorie oder
 * für alle. In der Antwort auf einen Vorgang stehen die aktiven zur Wahl, die
 * zu Postfach und Kategorie des Vorgangs passen; die Platzhalter füllt der
 * Server aus dem Vorgang. Was sich nicht füllen lässt, bleibt als [[name]]
 * sichtbar im Entwurf stehen.
 *
 * Lesen mit `support/sehen`, Pflegen mit `support/vorlagen`. Abschalten nimmt
 * einen Baustein aus der Wahl und lässt ihn stehen; Entfernen löscht ihn und
 * fragt vorher nach (Tabelle 29). Bearbeiten ab Tablet (ÄA-63).
 */

const GRENZE_TITEL = 200;
const GRENZE_BETREFF = 1000;
const GRENZE_TEXT = 20000;

const BEKANNT = new Set(PLATZHALTER.map((p) => p.name));

type Entwurf = {
  /** null: neu. */
  readonly id: string | null;
  /** Kennung; '' für alle Postfächer. */
  readonly postfach: string;
  /** '' für alle Kategorien. */
  readonly kategorie: Kategorie | '';
  readonly titel: string;
  readonly betreff: string;
  readonly text: string;
  readonly aktiv: boolean;
};

const LEER: Entwurf = { id: null, postfach: '', kategorie: '', titel: '', betreff: '', text: '', aktiv: true };

/**
 * SUP-20: Platzhalter, die der Server nicht kennt. Sie würden nie gefüllt —
 * ein Tippfehler wie {{kunde.nmae}} ginge sonst wörtlich an den Kunden.
 */
export function unbekanntePlatzhalter(...texte: readonly string[]): readonly string[] {
  const funde = new Set<string>();
  for (const t of texte) {
    for (const treffer of t.matchAll(/\{\{\s*([^{}]*?)\s*\}\}/g)) {
      const name = treffer[1] ?? '';
      if (!BEKANNT.has(name)) funde.add(`{{${name}}}`);
    }
  }
  return [...funde];
}

function alsEingabe(e: Entwurf): Bausteineingabe {
  return {
    postfach: e.postfach === '' ? null : e.postfach,
    kategorie: e.kategorie === '' ? null : e.kategorie,
    titel: e.titel.trim(),
    betreff: e.betreff.trim() === '' ? null : e.betreff.trim(),
    text: e.text,
    aktiv: e.aktiv,
  };
}

export function Textbausteine({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [liste, setzeListe] = useState<readonly Textbaustein[] | null>(null);
  const [postfaecher, setzePostfaecher] = useState<readonly Posteingangsstand[]>([]);
  const [filterPostfach, setzeFilterPostfach] = useState<string | null>(null);
  const [filterKategorie, setzeFilterKategorie] = useState<Kategorie | null>(null);
  const [filterAktiv, setzeFilterAktiv] = useState<boolean | null>(null);
  const [entwurf, setzeEntwurf] = useState<Entwurf | null>(null);
  const [entfernen, setzeEntfernen] = useState<Textbaustein | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [dialogfehler, setzeDialogfehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeBausteine({ postfach: filterPostfach, kategorie: filterKategorie, aktiv: filterAktiv }));
      setzeFehler(null);
    } catch (f) {
      setzeListe([]);
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [filterPostfach, filterKategorie, filterAktiv]);

  useEffect(() => {
    void holen();
  }, [holen]);

  useEffect(() => {
    posteingangsstand().then(
      (antwort) => {
        setzePostfaecher(antwort.postfaecher);
      },
      () => {
        setzePostfaecher([]);
      },
    );
  }, []);

  const postfachname = (kennung: string | null): string =>
    kennung === null ? 'alle Postfächer' : (postfaecher.find((p) => p.kennung === kennung)?.bezeichnung ?? kennung);

  async function speichern(e: Entwurf): Promise<void> {
    setzeLaeuft(true);
    setzeDialogfehler(null);
    try {
      const eingabe = alsEingabe(e);
      if (e.id === null) await bausteinAnlegen(eingabe);
      else await bausteinAendern(e.id, eingabe);
      setzeEntwurf(null);
      setzeMeldung(`„${eingabe.titel}" gespeichert.`);
      await holen();
    } catch (f) {
      setzeDialogfehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaeuft(false);
    }
  }

  const unbekannt = entwurf === null ? [] : unbekanntePlatzhalter(entwurf.betreff, entwurf.text);
  const vollstaendig = entwurf !== null && entwurf.titel.trim() !== '' && entwurf.text.trim() !== '';
  const aktive = (liste ?? []).filter((b) => b.aktiv).length;

  return (
    <AbTablet an={darfPflegen}>
      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}
      <Einstellungsseite>
        <Thema
          titel="Textbausteine"
          zusatz={liste === null ? undefined : `${String(aktive)} aktiv${liste.length > aktive ? `, ${String(liste.length - aktive)} abgeschaltet` : ''}`}
          breit
        >
          <p className="hilfe">
            Wiederkehrende Antworten im Posteingang. Ein Baustein gilt für ein Postfach oder für alle und
            für eine Kategorie oder für alle; beim Antworten stehen die aktiven zur Wahl, die zum Vorgang
            passen. Platzhalter wie {'{{kunde.name}}'} füllt das CRM aus dem Vorgang.
          </p>

          <div className="knopfreihe">
            <select
              className="liste__filter"
              aria-label="Postfach"
              value={filterPostfach ?? ''}
              onChange={(e) => {
                setzeFilterPostfach(e.target.value === '' ? null : e.target.value);
              }}
            >
              <option value="">Alle Postfächer</option>
              {postfaecher.map((p) => (
                <option key={p.kennung} value={p.kennung}>
                  {p.bezeichnung}
                </option>
              ))}
            </select>
            <select
              className="liste__filter"
              aria-label="Kategorie"
              value={filterKategorie ?? ''}
              onChange={(e) => {
                setzeFilterKategorie(e.target.value === '' ? null : (e.target.value as Kategorie));
              }}
            >
              <option value="">Alle Kategorien</option>
              {KATEGORIEN.map((k) => (
                <option key={k} value={k}>
                  {KATEGORIENAME[k]}
                </option>
              ))}
            </select>
            <select
              className="liste__filter"
              aria-label="Stand"
              value={filterAktiv === null ? '' : String(filterAktiv)}
              onChange={(e) => {
                setzeFilterAktiv(e.target.value === '' ? null : e.target.value === 'true');
              }}
            >
              <option value="">Aktive und abgeschaltete</option>
              <option value="true">Nur aktive</option>
              <option value="false">Nur abgeschaltete</option>
            </select>
          </div>

          {liste === null && <p role="status">Wird geladen …</p>}
          {liste !== null && liste.length === 0 && (
            <p className="liste__leer">
              {filterPostfach !== null || filterKategorie !== null || filterAktiv !== null
                ? 'Kein Textbaustein passt zum Filter.'
                : 'Noch kein Textbaustein angelegt.'}
            </p>
          )}
          {liste !== null && liste.length > 0 && (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label="Textbausteine">
                <thead>
                  <tr>
                    <th scope="col">Titel</th>
                    <th scope="col">Postfach</th>
                    <th scope="col">Kategorie</th>
                    <th scope="col">Stand</th>
                    <th scope="col">Geändert</th>
                    {darfPflegen && <th scope="col">Handlung</th>}
                  </tr>
                </thead>
                <tbody>
                  {liste.map((b) => (
                    <tr key={b.id} className={b.aktiv ? undefined : 'liste__zeile--blass'}>
                      <th scope="row">
                        {b.titel}
                        {b.betreff !== null && (
                          <>
                            <br />
                            <span className="hilfe">Betreff: {b.betreff}</span>
                          </>
                        )}
                      </th>
                      <td>{postfachname(b.postfach)}</td>
                      <td>{b.kategorie === null ? 'alle Kategorien' : KATEGORIENAME[b.kategorie]}</td>
                      <td>
                        {b.aktiv ? (
                          <span className={markenklasse('abschluss')}>aktiv</span>
                        ) : (
                          <span className={markenklasse('stillgelegt')}>abgeschaltet</span>
                        )}
                      </td>
                      <td>
                        <span className="liste__einzeilig">{alsZeitpunkt(b.geaendertAm) ?? b.geaendertAm}</span>
                      </td>
                      {darfPflegen && (
                        <td>
                          <div className="knopfreihe">
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              disabled={laeuft}
                              aria-label={`${b.titel} bearbeiten`}
                              onClick={() => {
                                setzeDialogfehler(null);
                                setzeEntwurf({
                                  id: b.id,
                                  postfach: b.postfach ?? '',
                                  kategorie: b.kategorie ?? '',
                                  titel: b.titel,
                                  betreff: b.betreff ?? '',
                                  text: b.text,
                                  aktiv: b.aktiv,
                                });
                              }}
                            >
                              Bearbeiten …
                            </button>
                            <button
                              type="button"
                              className="knopf knopf--eingriff knopf--klein"
                              disabled={laeuft}
                              aria-label={`${b.titel} entfernen`}
                              onClick={() => {
                                setzeEntfernen(b);
                              }}
                            >
                              Entfernen …
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {darfPflegen && (
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--klein"
                onClick={() => {
                  setzeDialogfehler(null);
                  setzeEntwurf({ ...LEER, postfach: filterPostfach ?? '', kategorie: filterKategorie ?? '' });
                }}
              >
                Textbaustein anlegen
              </button>
            </div>
          )}
        </Thema>
      </Einstellungsseite>

      {entwurf !== null && (
        <Dialog
          titel={entwurf.id === null ? 'Textbaustein anlegen' : 'Textbaustein bearbeiten'}
          unterzeile={entwurf.id === null ? undefined : entwurf.titel}
          breite="breit"
          beiSchliessen={() => {
            if (!laeuft) setzeEntwurf(null);
          }}
        >
          {dialogfehler !== null && <Meldung fehler={dialogfehler} />}
          <div className="feldreihe">
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="tb-postfach">
                Postfach
              </label>
              <select
                id="tb-postfach"
                value={entwurf.postfach}
                onChange={(e) => {
                  setzeEntwurf({ ...entwurf, postfach: e.target.value });
                }}
              >
                <option value="">Alle Postfächer</option>
                {postfaecher.map((p) => (
                  <option key={p.kennung} value={p.kennung}>
                    {p.bezeichnung}
                  </option>
                ))}
                {/* Ein Postfach, das der Abruf nicht (mehr) kennt, bleibt wählbar, statt still zu verschwinden. */}
                {entwurf.postfach !== '' && !postfaecher.some((p) => p.kennung === entwurf.postfach) && (
                  <option value={entwurf.postfach}>{entwurf.postfach}</option>
                )}
              </select>
            </div>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="tb-kategorie">
                Kategorie
              </label>
              <select
                id="tb-kategorie"
                value={entwurf.kategorie}
                onChange={(e) => {
                  setzeEntwurf({ ...entwurf, kategorie: e.target.value as Kategorie | '' });
                }}
              >
                <option value="">Alle Kategorien</option>
                {KATEGORIEN.map((k) => (
                  <option key={k} value={k}>
                    {KATEGORIENAME[k]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="tb-titel">
              Titel
            </label>
            <input
              id="tb-titel"
              type="text"
              maxLength={GRENZE_TITEL}
              value={entwurf.titel}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, titel: e.target.value });
              }}
            />
            <p className="feld__hilfe">So steht der Baustein in der Auswahl beim Antworten; der Kunde sieht ihn nicht.</p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="tb-betreff">
              Betreff (optional)
            </label>
            <input
              id="tb-betreff"
              type="text"
              maxLength={GRENZE_BETREFF}
              value={entwurf.betreff}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, betreff: e.target.value });
              }}
            />
            <p className="feld__hilfe">Leer: Die Antwort behält den Betreff des Vorgangs.</p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="tb-text">
              Text
            </label>
            <textarea
              id="tb-text"
              rows={10}
              maxLength={GRENZE_TEXT}
              value={entwurf.text}
              aria-invalid={unbekannt.length > 0}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, text: e.target.value });
              }}
            />
            {unbekannt.length > 0 && (
              <p className="feld__hilfe" role="alert">
                Unbekannter Platzhalter: {unbekannt.join(', ')} — er würde nicht gefüllt und ginge wörtlich hinaus.
              </p>
            )}
          </div>

          <Platzhalterhilfe />

          <div className="feld">
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={entwurf.aktiv}
                onChange={(e) => {
                  setzeEntwurf({ ...entwurf, aktiv: e.target.checked });
                }}
              />
              <span>Aktiv — beim Antworten zur Wahl</span>
            </label>
          </div>

          <Abschlussreihe
            abbruch={
              <button
                type="button"
                className="knopf"
                disabled={laeuft}
                onClick={() => {
                  setzeEntwurf(null);
                }}
              >
                Abbrechen
              </button>
            }
          >
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={laeuft || !vollstaendig || unbekannt.length > 0}
              onClick={() => void speichern(entwurf)}
            >
              {entwurf.id === null ? 'Anlegen' : 'Speichern'}
            </button>
          </Abschlussreihe>
        </Dialog>
      )}

      {/* Tabelle 29: Entfernen ist unumkehrbar und fragt nach. */}
      {entfernen !== null && (
        <Rueckfrage
          frage="Textbaustein entfernen?"
          unterzeile={entfernen.titel}
          tat="Entfernen"
          beiBestaetigen={async () => {
            await bausteinEntfernen(entfernen.id);
            setzeMeldung(`„${entfernen.titel}" entfernt.`);
            setzeEntfernen(null);
            await holen();
          }}
          beiSchliessen={() => {
            setzeEntfernen(null);
          }}
        >
          <p>
            Der Baustein wird gelöscht und steht beim Antworten nicht mehr zur Wahl. Das lässt sich
            nicht rückgängig machen. Gesendete Antworten bleiben, wie sie sind.
          </p>
          {entfernen.aktiv && (
            <p className="hilfe">Wer ihn nur vorübergehend nicht anbieten will, schaltet ihn unter „Bearbeiten" ab.</p>
          )}
        </Rueckfrage>
      )}
    </AbTablet>
  );
}

/** SUP-20: welche Platzhalter es gibt und was sie einsetzen. */
function Platzhalterhilfe(): JSX.Element {
  return (
    <div className="hinweiskarte">
      <p>
        <strong>Platzhalter</strong> — in Betreff und Text, mit doppelten geschweiften Klammern. Was der
        Vorgang nicht kennt, bleibt im Entwurf als [[name]] stehen und fällt vor dem Senden auf.
      </p>
      <dl>
        {PLATZHALTER.map((p) => (
          <div key={p.name}>
            <dt>
              <code>{`{{${p.name}}}`}</code>
            </dt>
            <dd className="hilfe">{p.bedeutung}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
